import {
  BallFinalMetricsConfiguratorV3,
  BallFinalMetricsViewerV3,
  BfMetricTileSize,
  IBallFinal,
  IBfMetricListItem,
  IStrikeZoneDecision,
  Spin3DFinal,
  StrikeZoneDefinitionEnum,
  UnitSystem,
} from '@trackman/baseball-shared-components';
import { IStrikeZonePoint } from '@trackman/baseball-shared-components/build/models/broadcast/IStrikeZonePoint';
import LDLoader from 'components/loader/LDLoader';
import { History } from 'history';
import { useStores } from 'index';
import { IPlayData } from 'interfaces/broadcast/IPlayData';
import { IStrikeZoneDecisionModel } from 'interfaces/session/IStrikeZoneDecision';
import { StrikeZoneLocation } from 'interfaces/session/IStrikeZoneDefinition';
import { uniqueId } from 'lodash';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { ListView } from './ListView/ListView';
import { MetricsTopBar } from './MetricsTopBar/MetricsTopBar';
import { StrikezoneView } from './StrikezoneView/StrikezoneView';
import { IStrikeZonePlate, getStrikeZonePlates } from './strikeZonePlates';
import { getStrikeZoneConfig, getStrikeZoneDecision } from './strikeZoneConfig';
import { MetricsPage } from './styles/MetricsStyles';

interface IProps {
  match?: any;
  location?: any;
  history?: History;
}

export enum LiveDashboardView {
  Dashboard = 'Dashboard',
  Strikezone = 'Strikezone',
  List = 'List',
}

export const Metrics: React.FC<IProps> = observer((props) => {
  const feedService = useStores().broadcastService;
  const liveDashboardStore = useStores().liveDashboardStore;
  const uiStore = useStores().uiStore;
  const { id } = props.match.params;
  const { metrics, tileSize, unitSystem } = liveDashboardStore;
  const [shouldUpdateList, setShouldUpdateList] = useState<string>();

  const gameStore = useStores().gameStore;
  const logService = useStores().logService;
  const permissionStore = useStores().permissionStore;
  const [isLoading, setIsLoading] = useState(true);
  const displayDataStore = useStores().displayDataStore;
  const [useStrikeZoneDecision, setUseStrikeZoneDecision] = useState(false);

  const strikezoneConfig = getStrikeZoneConfig(displayDataStore.displayData[0], gameStore.game, unitSystem);

  const getPermissions = () => {
    if (gameStore.game) {
      permissionStore
        .setPermissions({
          id: gameStore.game.id,
          gameDate: gameStore.game.gameDate,
          leagueId: gameStore.game.leagueId,
          levelId: gameStore.game.levelId,
          fieldId: gameStore.game.fieldId,
          venueId: gameStore.game.venueId,
          homeTeamId: gameStore.game.homeTeamId,
        })
        .then(() => {
          liveDashboardStore.applySpin3dPermission();
        })
        .then(() => {
          setIsLoading(false);
        });
    }
  };

  const fetchData = async () => {
    try {
      if (!gameStore.game) {
        const params = new URLSearchParams(props.location.search);

        if (params.get('isScheduled') === null) {
          return props.history.push('/');
        }

        await gameStore.setGameById(id, isScheduled, useSharingNetworkRules).then(() => {
          getPermissions();
        });
      } else {
        getPermissions();
      }

      gameStore.calculateHidePitchLocation().then(() => {
        liveDashboardStore.initializeMetricsFromLocalStorage(gameStore.hidePitchLocation);
      });
    } catch (error) {
      logService.logError({ error, message: `When getting game metadata.` });
    }
  };

  useEffect(() => {
    fetchData().then(() => {
      feedService.watch(gameStore.game.id, useSharingNetworkRules, onBallFinal, onSpin3dFinal, onPlayData, onStrikeZoneDecision);
    });
    return () => {
      feedService.stop();
      gameStore.clearGame();
      displayDataStore.clearGame();
      setUseStrikeZoneDecision(false);
    };
  }, []);

  const getAllStrikeZonePoints = () => {
    return displayDataStore.displayData.map((displayData) => {
      const priorityOrder = [StrikeZoneLocation.Front, StrikeZoneLocation.Middle, StrikeZoneLocation.Back];
      const data = displayData.strikeZoneDecision?.strikeZoneDecisionLocations.find((location) =>
        priorityOrder.includes(location.strikeZoneLocationType as StrikeZoneLocation)
      );
      return {
        index: displayData.index,
        ballX: data?.ballX || displayData.ballX,
        ballY: data?.ballY || displayData.ballY,
      };
    });
  };

  const getLastDisplayData = () => {
    if (displayDataStore.displayData.length > 0) {
      const lastDisplayData = displayDataStore.displayData[0];

      return lastDisplayData;
    }

    return undefined;
  };

  const getStrikeZonePointFromDisplayData = (): IStrikeZonePoint | undefined => {
    if (displayDataStore.displayData.length > 0) {
      const lastDisplayData = displayDataStore.displayData[0];
      const strikeZoneDecision = getStrikeZoneDecision(lastDisplayData.strikeZoneDecision as IStrikeZoneDecisionModel);

      return {
        ballX: strikeZoneDecision?.ballX || lastDisplayData.ballX,
        ballY: strikeZoneDecision?.ballY || lastDisplayData.ballY,
        isInZone: false,
      };
    }

    return undefined;
  };

  const useSharingNetworkRules = props.location.search.includes('sharingNetworkRules=true');
  const isScheduled = props.location.search.includes('isScheduled=true');

  const onBallFinal = (ballFinal: IBallFinal): void => {
    if (ballFinal.playId !== displayDataStore.currentPlayId) {
      displayDataStore.handleNewPlay(ballFinal.playId, [ballFinal], undefined, undefined, undefined);
    } else {
      displayDataStore.setBallFinals(ballFinal);
    }
  };

  const onSpin3dFinal = (spin3dFinal: Spin3DFinal): void => {
    if (spin3dFinal.playId !== displayDataStore.currentPlayId) {
      displayDataStore.handleNewPlay(spin3dFinal.playId, [], spin3dFinal, undefined, undefined);
    } else {
      displayDataStore.setSpin3dFinal(spin3dFinal);
    }
  };

  const onPlayData = (playData: IPlayData): void => {
    if (playData.playId !== displayDataStore.currentPlayId) {
      displayDataStore.handleNewPlay(undefined, [], undefined, undefined, playData);
    } else {
      displayDataStore.setPlayData(playData);
    }
  };

  const onStrikeZoneDecision = (strikeZoneDecision: IStrikeZoneDecision): void => {
    setUseStrikeZoneDecision(true);
    let szDecision = strikeZoneDecision;
    if (!szDecision.strikeZoneDecisionLocations?.length) {
      // Backward compatibility for older systems: fallback to front of homeplate
      szDecision.strikeZoneDecisionLocations = [
        {
          ballX: szDecision.ballX,
          ballY: szDecision.ballY,
          dimensions: szDecision.dimensions,
          ballContactArea: szDecision.isInZone ? 'Inside' : 'Outside',
          strikeZoneLocationType: 'Front',
        },
      ];
    }
    if (strikeZoneDecision.playId !== displayDataStore.currentPlayId) {
      displayDataStore.handleNewPlay(strikeZoneDecision.playId, [], undefined, strikeZoneDecision, undefined);
    } else {
      displayDataStore.setLastStrikeZoneDecision(strikeZoneDecision);
    }
  };

  useEffect(() => {
    document.querySelector('body')?.setAttribute('data-settings', liveDashboardStore.isSettingsMenuVisible.toString());
  }, [liveDashboardStore.isSettingsMenuVisible]);

  const onConfigChangeHandler = (newMetrics: IBfMetricListItem[], unitSystem: UnitSystem, tileSize: BfMetricTileSize) => {
    liveDashboardStore.setMetrics(newMetrics);

    setShouldUpdateList(uniqueId());
    liveDashboardStore.setTileSize(tileSize);
    liveDashboardStore.setUnitSystem(unitSystem);
  };

  const getPlayerName = (playerName?: string) => {
    if (playerName && playerName.includes(', ')) {
      const [secondName, firstName] = playerName.split(', ');
      return `${firstName} ${secondName}`;
    }

    return playerName;
  };

  const getPlates = (): IStrikeZonePlate[] => {
    if (useStrikeZoneDecision) {
      return getStrikeZonePlates(getLastDisplayData()?.strikeZoneDecision);
    }

    // Fallback to front of homeplate from BallFinal
    return [
      {
        id: 0,
        strikeZoneCalculationType: 'Front',
        offset: 0,
        extension: 0,
      },
    ];
  };

  const getBallPositions = (plate: 'Front' | 'Middle' | 'Back') => {
    const positions = getLastDisplayData()?.strikeZoneDecision?.strikeZoneDecisionLocations.find((x) => x.strikeZoneLocationType === plate);

    if (positions) {
      return {
        ballX: positions.ballX,
        ballY: positions.ballY,
        ballContactArea: positions.ballContactArea,
      };
    }

    return undefined;
  };

  const getFrontBallPosition = () => {
    if (useStrikeZoneDecision) {
      return getBallPositions('Front');
    }

    // Fallback to front of homeplate from BallFinal
    const lastDisplayData = getLastDisplayData();

    if (lastDisplayData) {
      return {
        ballX: lastDisplayData.ballX,
        ballY: lastDisplayData.ballY,
        ballContactArea: lastDisplayData.strikeZoneDecision?.isInZone ? 'Inside' : 'Outside',
      };
    }

    return undefined;
  };

  return (
    <div>
      {isLoading ? (
        <LDLoader size='large' style={{ height: 'calc(100vh - 200px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
      ) : (
        <>
          <MetricsPage>
            <MetricsTopBar />
            <div className='container'>
              <BallFinalMetricsConfiguratorV3
                metrics={metrics}
                tileSize={tileSize}
                unitSystem={liveDashboardStore.unitSystem}
                onConfigChanged={onConfigChangeHandler}
                onHide={(): void => liveDashboardStore.setIsSettingsMenuVisible(false)}
                isVisible={liveDashboardStore.isSettingsMenuVisible}
                darkMode={liveDashboardStore.darkMode}
                width='300px'
                disableStrikeZoneDecision
                shouldUpdateList={shouldUpdateList}
              />
              {uiStore.currentView === LiveDashboardView.Dashboard && (
                <BallFinalMetricsViewerV3
                  metrics={metrics.filter((x) => x.isChecked)}
                  tileSize={tileSize}
                  unitSystem={unitSystem}
                  ballFinals={displayDataStore.ballFinals}
                  strikeZonePoint={getStrikeZonePointFromDisplayData()}
                  disableStrikeZoneDecision
                  darkMode={liveDashboardStore.darkMode}
                  strikeZoneConfig={strikezoneConfig}
                  spin3dFinal={displayDataStore.spin3dFinal}
                  playerName={getPlayerName(displayDataStore.playData?.batter.name)}
                  strikeZoneCalculationInformations={getPlates()}
                  strikeZonePoints={{
                    front: getFrontBallPosition(),
                    middle: getBallPositions('Middle'),
                    back: getBallPositions('Back'),
                  }}
                />
              )}
              {uiStore.currentView === LiveDashboardView.Strikezone && (
                <StrikezoneView
                  data={getAllStrikeZonePoints()}
                  darkMode={liveDashboardStore.darkMode}
                  config={strikezoneConfig}
                />
              )}
              {uiStore.currentView === LiveDashboardView.List && (
                <ListView
                  data={displayDataStore.displayData}
                  metrics={metrics.filter((x) => x.isChecked)}
                  unitSystem={unitSystem}
                  itemCounter={displayDataStore.itemCounter}
                />
              )}
            </div>
          </MetricsPage>
        </>
      )}
    </div>
  );
});
